import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  CreditCard,
  Loader2,
  Receipt,
  Shield,
} from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { getMyPortalSummary } from "@/lib/member-portal.functions";
import { listMyStripeInvoices } from "@/lib/membership.functions";
import { getPlan } from "@/lib/membership-plans";
import { formatMoney } from "@/lib/money";
import { useHideCommerce } from "@/lib/native-app";

export const Route = createFileRoute("/portal")({
  head: () => ({
    meta: [
      { title: "Member Portal | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Recovery Trauma™ member portal: your membership and renewal dates, your payment records, and your saved journal entries.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PortalPage,
});

type Payment = {
  key: string;
  date: string | null;
  label: string;
  amount: string | null;
  status: string | null;
  source: string;
  url?: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  registered_unpaid: "Registered — no payment yet",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  past_due: "Payment issue — please check your card",
  payment_failed: "Signed up — payment didn't go through",
  paused: "Paused",
  expired: "Expired",
  refunded: "Refunded",
};

function fmtDate(value: string | number | null | undefined): string | null {

  if (value === null || value === undefined) return null;
  const d = typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function Card({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon: typeof Receipt;
  action?: { to: string; label: string };
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[22px] border border-gold/25 bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 font-display text-xl text-foreground">
          <Icon className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
          {title}
        </h2>
        {action && (
          <Link
            to={action.to}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
          >
            {action.label} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        )}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gold/15 py-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

function PortalPage() {
  const { loading, isAuthed, isAdmin, displayName } = useMembership();
  const fetchSummary = useServerFn(getMyPortalSummary);
  const fetchStripeInvoices = useServerFn(listMyStripeInvoices);

  const inApp = useHideCommerce();
  const summary = useQuery({
    queryKey: ["portal-summary"],
    queryFn: () => fetchSummary({ data: {} as never }),
    enabled: isAuthed,
  });

  const stripeInvoices = useQuery({
    queryKey: ["portal-stripe-invoices"],
    queryFn: () => fetchStripeInvoices({ data: {} as never }),
    enabled: isAuthed,
    retry: false,
  });

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl text-foreground">Your member portal</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Sign in to see your membership, your renewal dates, your payment records and your saved
          journal entries — all in one place.
        </p>
        <Link
          to="/auth"
          className="mt-6 inline-flex min-h-[48px] items-center rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)]"
        >
          Sign in
        </Link>
      </main>
    );
  }

  const data = summary.data;
  const m = data?.membership ?? null;
  const paypal = data?.paypal ?? null;
  const plan = m?.plan_id ? getPlan(m.plan_id) : null;

  const renewalRaw = m?.next_billing_date ?? m?.current_period_end ?? paypal?.next_billing_time ?? null;
  const renewal = fmtDate(renewalRaw);
  const cancelling = Boolean(m?.cancel_at_period_end) || Boolean(paypal?.cancelled_at);

  const payments: Payment[] = [
    ...(data?.payments ?? []).map((p) => ({
      key: `inv-${p.id}`,
      date: fmtDate(p.issued_at ?? p.created_at),
      label: p.description ?? p.invoice_number ?? "Membership payment",
      amount:
        typeof p.amount_cents === "number"
          ? formatMoney(p.amount_cents, (p.currency ?? "usd").toUpperCase(), { fromCents: true })
          : null,
      status: null,
      source: (p.provider ?? "").toLowerCase() === "paypal" ? "PayPal" : "Card",
      url: null,
    })),
    ...((stripeInvoices.data ?? []).map((inv) => ({
      key: `stripe-${inv.id}`,
      date: fmtDate(inv.created),
      label: inv.description ?? inv.number ?? "Membership payment",
      amount: formatMoney(inv.amount_paid || inv.amount_due, inv.currency.toUpperCase(), {
        fromCents: true,
      }),
      status: inv.status,
      source: "Card",
      url: inv.hosted_invoice_url ?? inv.invoice_pdf,
    })) as Payment[]),
  ];

  const journal = data?.journal;

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <h1 className="font-display text-2xl text-foreground sm:text-3xl">
        Your member portal
      </h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Welcome back{displayName ? `, ${displayName}` : ""}. Everything about your own membership,
        payments and journal in one place. Only you can see this.
      </p>

      {isAdmin && (
        <Link
          to="/admin"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold text-foreground hover:border-gold"
        >
          <Shield className="h-3.5 w-3.5" aria-hidden />
          Open the admin dashboard
        </Link>
      )}

      {summary.isLoading ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : summary.isError ? (
        <p className="mt-8 rounded-2xl border border-gold/25 bg-card p-5 text-sm text-muted-foreground">
          We couldn't load your details just now. Please refresh the page, or contact us if it keeps
          happening.
        </p>
      ) : (
        <div className="mt-6 space-y-5">
          {/* ── Membership & renewals ── */}
          <Card title="Membership & renewals" icon={CalendarDays} action={{ to: "/billing", label: "Manage membership" }}>
            {m ? (
              <div>
                <Row label="Plan" value={plan?.shortName ?? "Recovery Trauma™ Membership"} />
                <Row
                  label="Status"
                  value={
                    m.complimentary_grant
                      ? "Complimentary access"
                      : m.status === "active"
                        ? cancelling
                          ? "Active — ends at the end of this period"
                          : "Active"
                        : STATUS_LABELS[m.status ?? ""] ?? "Not active yet"
                  }
                />
                <Row label="Member since" value={fmtDate(m.activated_at ?? m.paid_at) ?? "—"} />
                <Row
                  label={cancelling ? "Access until" : "Next renewal"}
                  value={renewal ?? "—"}
                />
                {!cancelling && m.status === "active" && !m.complimentary_grant && typeof m.amount_paid_cents === "number" && m.amount_paid_cents > 0 && (
                  <Row
                    label="Renewal amount"
                    value={formatMoney(m.amount_paid_cents, (m.currency ?? "usd").toUpperCase(), { fromCents: true })}
                  />
                )}
                {m.cancelled_at && <Row label="Cancelled on" value={fmtDate(m.cancelled_at) ?? "—"} />}
                {(m.status === "payment_failed" || m.status === "past_due") && (
                  <div className="mt-3 rounded-xl border border-gold/30 bg-gold/10 p-3 text-xs leading-relaxed text-foreground">
                    Your last payment didn't go through, so nothing was charged. Your account and saved
                    work are safe.{" "}
                    <Link to="/billing" className="font-semibold text-[var(--gold-deep)] underline underline-offset-4">
                      Try your payment again
                    </Link>
                  </div>
                )}
                {!m.complimentary_grant && (m.status !== "active" || cancelling) && (
                  <RenewButton
                    to={inApp ? "/app-membership" : cancelling ? "/billing" : "/choose-membership"}
                    label={cancelling ? "Keep my membership" : "Renew my membership"}
                  />
                )}
                {cancelling && (
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    Your membership won't renew again. You keep full access until the date above,
                    and your journals, progress and saved work stay in your account.
                  </p>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  We don't have an active membership recorded for this account yet.
                </p>
                <RenewButton to={inApp ? "/app-membership" : "/choose-membership"} label="Renew my membership" />
              </>
            )}
          </Card>

          {/* ── Payments ── */}
          <Card
            title="Your payments"
            icon={Receipt}
            action={{ to: "/payment-history", label: "Full payment history" }}
          >
            {stripeInvoices.isLoading && payments.length === 0 ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : payments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No payments are recorded on this account yet.
              </p>
            ) : (
              <ul className="divide-y divide-gold/15">
                {payments.map((p) => (
                  <li key={p.key} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <span>
                      <span className="block text-sm font-medium text-foreground">{p.label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[p.date, p.source, p.status].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-foreground">{p.amount ?? "—"}</span>
                      {p.url && (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
                        >
                          Receipt
                        </a>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {stripeInvoices.isError && (
              <p className="mt-3 text-xs text-muted-foreground">
                Card receipts couldn't be loaded just now — any payments recorded in your account
                are still shown above.
              </p>
            )}
          </Card>

          {/* ── Journal ── */}
          <Card title="Your journal entries" icon={BookOpen} action={{ to: "/journal", label: "Open your journal" }}>
            {journal && journal.total > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {journal.total} saved {journal.total === 1 ? "entry" : "entries"}, private to you.
                </p>
                <ul className="mt-3 divide-y divide-gold/15">
                  {journal.recent.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                      <span className="text-sm font-medium text-foreground">
                        {e.title || "Untitled entry"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {[fmtDate(e.created_at), e.mood].filter(Boolean).join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                You haven't saved any journal entries yet. Whenever you'd like to, your journal is
                private and always yours.
              </p>
            )}
          </Card>

          <Card title="Other account areas" icon={CreditCard}>
            <ul className="grid gap-2 sm:grid-cols-2">
              {[
                { to: "/my-membership", label: "My membership hub" },
                { to: "/profile", label: "Profile & details" },
                { to: "/preferences", label: "Email preferences" },
                { to: "/my-events", label: "My events" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="inline-flex w-full items-center justify-between rounded-2xl border border-gold/25 px-4 py-3 text-sm font-medium text-foreground hover:border-gold"
                  >
                    {l.label} <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </main>
  );
}

function RenewButton({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to as never}
      className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 font-display text-base font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5"
    >
      <CreditCard className="h-4 w-4" aria-hidden="true" />
      {label}
    </Link>
  );
}
